// src/policy-similarity-matcher.js

/**
 * ===========================================
 * Configuration & Debug Utilities
 * ===========================================
 */

// Toggle debug logging - set to true for development, false for production
const DEBUG = false;

/**
 * Controlled debug logging wrapper
 */
const debugLog = (message, data = null) => {
    if (DEBUG) {
        const timestamp = new Date().toISOString().split('T')[1].slice(0, 8);
        console.log(`[${timestamp}] ${message}`, data || '');
    }
};

/**
 * Safe string lowercase conversion
 */
const safeLower = (str) => (typeof str === 'string' ? str.toLowerCase() : '');

/**
 * Normalize display name for comparison (remove special chars, lowercase)
 */
const normalizeDisplayName = (str) =>
    (str || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');

/**
 * ===========================================
 * Deep Equality Comparison (Order-Insensitive for Objects)
 * ===========================================
 */

/**
 * Deep equality check that handles objects, arrays, primitives
 * - Objects: Order-insensitive (keys compared regardless of order)
 * - Arrays: Order-sensitive (index matters for policy conditions)
 * - Handles null, undefined, nested structures
 */
const deepEqual = (obj1, obj2) => {
    // Identical references
    if (obj1 === obj2) return true;

    // Null or undefined checks
    if (obj1 == null || obj2 == null) return obj1 === obj2;

    // Type mismatch
    if (typeof obj1 !== typeof obj2) return false;

    // Primitive types
    if (typeof obj1 !== 'object') return obj1 === obj2;

    // Array comparison (order matters for policy conditions)
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return false;
        return obj1.every((val, idx) => deepEqual(val, obj2[idx]));
    }

    // One is array, other is not
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

    // Object comparison (order-insensitive)
    const keys1 = Object.keys(obj1).sort();
    const keys2 = Object.keys(obj2).sort();

    if (keys1.length !== keys2.length) return false;
    if (keys1.join(',') !== keys2.join(',')) return false;

    return keys1.every(key => deepEqual(obj1[key], obj2[key]));
};

/**
 * ===========================================
 * Singleton & Type Detection
 * ===========================================
 */

/**
 * Identify singleton/global policies that should always match by identity
 */
const isSingletonPolicy = (policy) => {
    if (!policy) return false;

    // Teams/Exchange Global settings
    if (policy.Identity === "Global" || policy.ConfigId === "Global") {
        return true;
    }

    // Authentication Methods Policy (only one per tenant)
    if (safeLower(policy.id) === "authenticationmethodspolicy") {
        return true;
    }

    // SharePoint tenant settings (no user-controlled displayName)
    if (policy['@odata.context']?.includes('admin/sharepoint/settings')) {
        return true;
    }

    // Any policy from a singleton endpoint
    if (policy.isSingleton === true) {
        return true;
    }

    // Check for other singleton indicators
    const singletonIndicators = [
        'authenticationmethodspolicy',
        'authenticationstrengthpolicy',
        'crosstenantaccesspolicy',
        'identitysecuritydefaultsenforcementpolicy'
    ];

    const odataType = safeLower(policy['@odata.type']);
    return singletonIndicators.some(indicator => odataType.includes(indicator));
};

/**
 * Detect the type of policy based on its structure
 */
const detectPolicyType = (policy) => {
    if (!policy) return 'unknown';

    // Singleton policies (check first)
    if (isSingletonPolicy(policy)) {
        return 'singleton';
    }

    // ✅ CRITICAL: Check @odata.type FIRST before any other structural checks
    // This ensures Intune policies are correctly identified
    if (policy['@odata.type']) {
        const odataType = safeLower(policy['@odata.type']);

        // Intune Compliance Policies
        if (odataType.includes('compliancepolicy')) {
            debugLog('✅ Detected as intuneCompliance:', policy.displayName);
            return 'intuneCompliance';
        }

        // Intune Configuration Policies
        if (odataType.includes('configuration') || odataType.includes('devicemanagementconfiguration')) {
            debugLog('✅ Detected as intuneConfiguration:', policy.displayName);
            return 'intuneConfiguration';
        }

        // Authentication Methods Policy
        if (odataType.includes('authenticationmethodspolicy')) {
            return 'authenticationMethods';
        }
    }

    // Conditional Access Policy
    // Check for conditions OR grantControls OR sessionControls
    // AND ensure templateId is null/undefined (not a template-based policy)
    if (
        (policy.conditions || policy.grantControls || policy.sessionControls) &&
        (policy.templateId === null || policy.templateId === undefined)
    ) {
        debugLog('✅ Detected as conditionalAccess:', policy.displayName);
        return 'conditionalAccess';
    }

    // Group Settings (Group.Unified)
    if (policy.templateId && policy.values && Array.isArray(policy.values)) {
        return 'groupSettings';
    }

    // Intune Configuration Policies (fallback check)
    if (policy.settings && policy.templateReference) {
        return 'intuneConfiguration';
    }

    // Exchange SafeLinks/SafeAttachments
    if (policy.EnableSafeLinksForEmail !== undefined || policy.EnableSafeLinksForTeams !== undefined) {
        return 'exchangeSafeLinks';
    }

    // Teams Settings
    if (policy.AllowEmailIntoChannel !== undefined || policy.AllowFederatedUsers !== undefined) {
        return 'teamsSettings';
    }

    // SharePoint Settings
    if (policy.sharingCapability !== undefined || policy.isLoopEnabled !== undefined) {
        return 'sharepointSettings';
    }

    // Exchange Organization Config
    if (
        policy.OrganizationId || 
        policy.ExchangeObjectId || 
        policy.ServicePlan ||
        (policy.OrganizationalUnitRoot && policy.LegacyExchangeDN)
    ) {
        debugLog('✅ Detected as exchangeOrganizationConfig:', policy.DisplayName || policy.Name);
        return 'exchangeOrganizationConfig';
    }

    // Azure AD Authorization Policy
    if (
        policy.allowInvitesFrom !== undefined &&
        policy.guestUserRoleId !== undefined &&
        policy.defaultUserRolePermissions !== undefined
    ) {
        debugLog('✅ Detected as azureADAuthorizationPolicy:', policy.displayName);
        return 'azureADAuthorizationPolicy';
    }

    debugLog('⚠️ Could not detect policy type, defaulting to generic:', policy.displayName || policy.name);
    return 'generic';
};

/**
 * Check if two policies are the same type
 */
const areSameType = (policy1, policy2) => {
    const type1 = detectPolicyType(policy1);
    const type2 = detectPolicyType(policy2);

    // ✅ For Intune policies, also check @odata.type must match exactly
    if (type1 === 'intuneCompliance' && type2 === 'intuneCompliance') {
        const odataMatch = policy1['@odata.type'] === policy2['@odata.type'];
        if (!odataMatch) {
            debugLog(`⚠️ Intune compliance type mismatch: ${policy1['@odata.type']} vs ${policy2['@odata.type']}`);
        }
        return odataMatch;
    }

    if (type1 === 'intuneConfiguration' && type2 === 'intuneConfiguration') {
        const odataMatch = policy1['@odata.type'] === policy2['@odata.type'];
        if (!odataMatch) {
            debugLog(`⚠️ Intune configuration type mismatch: ${policy1['@odata.type']} vs ${policy2['@odata.type']}`);
        }
        return odataMatch;
    }

    return type1 === type2 && type1 !== 'unknown';
};

/**
 * ===========================================
 * Main Similarity Calculator (Router)
 * ===========================================
 */

/**
 * Calculate a similarity score between two policies
 */
export const calculatePolicySimilarity = (baselinePolicy, clientPolicy) => {
    // Singleton policies always match by identity
    if (isSingletonPolicy(baselinePolicy) && isSingletonPolicy(clientPolicy)) {
        return {
            conditions: 100,
            grantControls: 100,
            sessionControls: 100,
            state: 100,
            overall: 100,
            isSingleton: true
        };
    }

    // Check if same type first
    if (!areSameType(baselinePolicy, clientPolicy)) {
        return {
            conditions: 0,
            grantControls: 0,
            sessionControls: 0,
            state: 0,
            overall: 0,
            mismatchReason: 'Different policy types'
        };
    }

    const policyType = detectPolicyType(baselinePolicy);

    // Route to appropriate comparison function
    switch (policyType) {
        case 'conditionalAccess':
            return compareConditionalAccessPolicies(baselinePolicy, clientPolicy);

        case 'intuneCompliance':
            return compareIntuneCompliancePolicies(baselinePolicy, clientPolicy);

        case 'intuneConfiguration':
            return compareIntuneConfigurationPolicies(baselinePolicy, clientPolicy);

        case 'authenticationMethods':
            return compareAuthenticationMethodsPolicies(baselinePolicy, clientPolicy);

        case 'exchangeSafeLinks':
            return compareExchangePolicies(baselinePolicy, clientPolicy);

        case 'exchangeOrganizationConfig':
            return compareExchangeOrganizationConfig(baselinePolicy, clientPolicy);

        case 'azureADAuthorizationPolicy':
            return compareAzureADAuthorizationPolicy(baselinePolicy, clientPolicy);

        case 'teamsSettings':
        case 'sharepointSettings':
            return compareSettingsPolicies(baselinePolicy, clientPolicy);

        case 'groupSettings':
            return compareGroupSettings(baselinePolicy, clientPolicy);

        default:
            return compareGenericPolicies(baselinePolicy, clientPolicy);
    }
};

/**
 * ===========================================
 * Display Name Comparison (Fuzzy Matching)
 * ===========================================
 */

/**
 * Compare display names with fuzzy matching using Levenshtein distance
 */
const compareDisplayNames = (name1, name2) => {
    if (!name1 || !name2) return 0;

    // Exact match = 100%
    if (name1 === name2) return 100;

    // Normalize for comparison
    const norm1 = normalizeDisplayName(name1);
    const norm2 = normalizeDisplayName(name2);

    // Exact match after normalization = 100%
    if (norm1 === norm2) return 100;

    // Calculate Levenshtein distance similarity
    const maxLen = Math.max(norm1.length, norm2.length);
    if (maxLen === 0) return 100; // Both empty strings

    const distance = levenshteinDistance(norm1, norm2);
    const similarity = Math.round(((maxLen - distance) / maxLen) * 100);

    return similarity;
};

/**
 * Calculate Levenshtein distance between two strings
 */
const levenshteinDistance = (str1, str2) => {
    const matrix = [];

    // Initialize first column
    for (let i = 0; i <= str2.length; i++) {
        matrix[i] = [i];
    }

    // Initialize first row
    for (let j = 0; j <= str1.length; j++) {
        matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= str2.length; i++) {
        for (let j = 1; j <= str1.length; j++) {
            if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1,     // insertion
                    matrix[i - 1][j] + 1      // deletion
                );
            }
        }
    }

    return matrix[str2.length][str1.length];
};

/**
 * ===========================================
 * Type-Specific Policy Comparisons
 * ===========================================
 */

/**
 * Compare Conditional Access Policies
 */
const compareConditionalAccessPolicies = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 0,
        state: 0,
        displayName: 0,
        overall: 0,
        stateMismatch: false
    };

    const weights = {
        displayName: 0.20,
        conditions: 0.40,
        grantControls: 0.25,
        sessionControls: 0.10,
        state: 0.05
    };

    // ✅ Compare display names with fuzzy matching
    scores.displayName = compareDisplayNames(
        baseline.displayName,
        client.displayName
    );

    // ✅ Always calculate all scores (don't return early)
    scores.conditions = compareConditions(baseline.conditions, client.conditions);
    scores.grantControls = compareGrantControls(baseline.grantControls, client.grantControls);
    scores.sessionControls = compareSessionControls(baseline.sessionControls, client.sessionControls);

    // Compare state (enabled/disabled)
    if (baseline.state === client.state) {
        scores.state = 100;
    } else {
        scores.state = 0;
        scores.stateMismatch = true;
    }

    // ✅ If display names match exactly AND everything else matches, perfect score
    if (scores.displayName === 100 &&
        scores.conditions === 100 &&
        scores.grantControls === 100 &&
        scores.sessionControls === 100) {
        debugLog('🎯 PERFECT MATCH - Identical policies!');
        scores.overall = 100;
        return scores;
    }

    // Calculate weighted overall score
    scores.overall = Math.round(
        scores.displayName * weights.displayName +
        scores.conditions * weights.conditions +
        scores.grantControls * weights.grantControls +
        scores.sessionControls * weights.sessionControls +
        scores.state * weights.state
    );

    return scores;
};

/**
 * Compare Intune Compliance Policies
 */
const compareIntuneCompliancePolicies = (baseline, client) => {
    const scores = {
        passwordSettings: 0,
        securitySettings: 0,
        osSettings: 0,
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0,
        isIntunePolicy: true,
        // ✅ Add custom labels for this policy type
        scoreLabels: {
            conditions: 'Password Requirements',
            grantControls: 'Security Requirements',
            sessionControls: 'OS Requirements'
        },
        policyTypeLabel: 'Intune Compliance'
    };

    // Password-related settings
    const passwordFields = [
        'passwordRequired', 'passwordMinimumLength', 'passwordRequiredType',
        'passwordMinutesOfInactivityBeforeLock', 'passwordExpirationDays',
        'passwordPreviousPasswordBlockCount', 'requiredPasswordComplexity',
        'passcodeRequired', 'passcodeMinimumLength', 'passcodeRequiredType',
        'passcodeMinutesOfInactivityBeforeLock', 'passcodeExpirationDays',
        'passcodePreviousPasscodeBlockCount', 'passcodeBlockSimple'
    ];

    // Security-related settings
    const securityFields = [
        'securityBlockJailbrokenDevices', 'deviceThreatProtectionEnabled',
        'deviceThreatProtectionRequiredSecurityLevel', 'storageRequireEncryption',
        'securityRequireSafetyNetAttestationBasicIntegrity', 'securityRequireVerifyApps',
        'securityDisableUsbDebugging', 'securityPreventInstallAppsFromUnknownSources',
        'systemIntegrityProtectionEnabled', 'firewallEnabled'
    ];

    // OS-related settings
    const osFields = [
        'osMinimumVersion', 'osMaximumVersion', 'osMinimumBuildVersion', 'osMaximumBuildVersion',
        'minAndroidSecurityPatchLevel'
    ];

    // Calculate scores using helper function
    scores.passwordSettings = compareIntuneFields(baseline, client, passwordFields);
    scores.securitySettings = compareIntuneFields(baseline, client, securityFields);
    scores.osSettings = compareIntuneFields(baseline, client, osFields);

    // Set conditions and grantControls to match component scores for UI consistency
    scores.conditions = scores.passwordSettings;
    scores.grantControls = scores.securitySettings;

    // Calculate weighted overall score
    const weights = {
        passwordSettings: 0.40,
        securitySettings: 0.40,
        osSettings: 0.20
    };

    scores.overall = Math.round(
        scores.passwordSettings * weights.passwordSettings +
        scores.securitySettings * weights.securitySettings +
        scores.osSettings * weights.osSettings
    );

    return scores;
};

/**
 * Helper function to compare Intune policy fields using deep equality
 */
const compareIntuneFields = (baseline, client, fields) => {
    let matching = 0;
    let total = 0;

    fields.forEach(field => {
        if (baseline[field] !== undefined || client[field] !== undefined) {
            total++;
            // ✅ Use deepEqual instead of simple equality for complex values
            if (deepEqual(baseline[field], client[field])) {
                matching++;
            }
        }
    });

    return total > 0 ? Math.round((matching / total) * 100) : 100;
};

/**
 * Compare Intune Configuration Policies
 */
const compareIntuneConfigurationPolicies = (baseline, client) => {
    const scores = {
        settingsMatch: 0,
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0,
        isIntunePolicy: true,
        // ✅ Add custom labels for this policy type
        scoreLabels: {
            conditions: 'Configuration Settings',
            grantControls: 'Policy Settings',
            sessionControls: 'Template Match'
        },
        policyTypeLabel: 'Intune Configuration'
    };

    // Compare template reference
    if (baseline.templateReference?.templateId && client.templateReference?.templateId) {
        scores.templateMatch = baseline.templateReference.templateId === client.templateReference.templateId ? 100 : 0;
    }

    // Compare settings array using deep equality
    if (baseline.settings && client.settings) {
        // ✅ Use deepEqual instead of JSON.stringify
        const settingsMatch = deepEqual(baseline.settings, client.settings);
        scores.settingsMatch = settingsMatch ? 100 : 0;
    }

    // Set conditions and grantControls for UI consistency
    scores.conditions = scores.settingsMatch;
    scores.grantControls = scores.settingsMatch;

    // Average all relevant scores
    scores.overall = scores.settingsMatch;

    return scores;
};

/**
 * Compare Authentication Methods Policies
 */
const compareAuthenticationMethodsPolicies = (baseline, client) => {
    const scores = {
        overall: 0,
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100
    };

    if (baseline.authenticationMethodConfigurations && client.authenticationMethodConfigurations) {
        // ✅ Use deepEqual instead of JSON.stringify
        const similarity = deepEqual(
            baseline.authenticationMethodConfigurations,
            client.authenticationMethodConfigurations
        );
        scores.overall = similarity ? 100 : 0;
        scores.conditions = scores.overall;
        scores.grantControls = scores.overall;
    }

    return scores;
};

/**
 * Compare Exchange SafeLinks/SafeAttachments Policies
 */
const compareExchangePolicies = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0
    };

    const settingsToCompare = [
        'EnableSafeLinksForEmail', 'EnableSafeLinksForTeams', 'EnableSafeLinksForOffice',
        'TrackClicks', 'AllowClickThrough', 'ScanUrls', 'EnableForInternalSenders',
        'DeliverMessageAfterScan', 'DisableUrlRewrite'
    ];

    let matchingSettings = 0;
    let totalSettings = 0;

    settingsToCompare.forEach(setting => {
        if (baseline[setting] !== undefined || client[setting] !== undefined) {
            totalSettings++;
            // ✅ Use deepEqual for consistency (handles edge cases)
            if (deepEqual(baseline[setting], client[setting])) {
                matchingSettings++;
            }
        }
    });

    scores.overall = totalSettings > 0 ? Math.round((matchingSettings / totalSettings) * 100) : 0;
    scores.conditions = scores.overall;
    scores.grantControls = scores.overall;

    return scores;
};

/**
 * Compare Settings Policies (Teams, SharePoint, etc.)
 */
const compareSettingsPolicies = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0
    };

    // Compare all boolean/string settings
    let matchingSettings = 0;
    let totalSettings = 0;

    const allKeys = new Set([...Object.keys(baseline), ...Object.keys(client)]);

    allKeys.forEach(key => {
        // Skip metadata fields
        if (key === 'Identity' || key === 'ConfigId' || key === 'ConfigMetadata' ||
            key === '@odata.context' || key.includes('Guid') || key.includes('Id')) {
            return;
        }

        if (baseline[key] !== undefined || client[key] !== undefined) {
            totalSettings++;
            // ✅ Use deepEqual instead of JSON.stringify (handles object key ordering)
            if (deepEqual(baseline[key], client[key])) {
                matchingSettings++;
            }
        }
    });

    scores.overall = totalSettings > 0 ? Math.round((matchingSettings / totalSettings) * 100) : 0;
    scores.conditions = scores.overall;
    scores.grantControls = scores.overall;

    return scores;
};

/**
 * Compare Group Settings
 */
const compareGroupSettings = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0
    };

    if (baseline.templateId === client.templateId && baseline.values && client.values) {
        // ✅ Use deepEqual instead of JSON.stringify
        const similarity = deepEqual(baseline.values, client.values);
        scores.overall = similarity ? 100 : 50;
        scores.conditions = scores.overall;
        scores.grantControls = scores.overall;
    }

    return scores;
};

/**
 * Compare Exchange Organization Configuration
 */
const compareExchangeOrganizationConfig = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0,
        // ✅ Add custom labels for this policy type
        scoreLabels: {
            conditions: 'Critical Settings',
            grantControls: 'Security Settings',
            sessionControls: 'Feature Settings'
        },
        policyTypeLabel: 'Exchange Organization'
    };

    // Key Exchange settings to compare
    const criticalFields = [
        'ActivityBasedAuthenticationTimeoutEnabled',
        'ActivityBasedAuthenticationTimeoutInterval',
        'OAuth2ClientProfileEnabled',
        'MapiHttpEnabled',
        'PublicFoldersEnabled',
        'AutoExpandingArchiveEnabled',
        'AuditDisabled',
        'CustomerLockboxEnabled',
        'BlockMoveMessagesForGroupFolders'
    ];

    // Security-related fields
    const securityFields = [
        'RejectDirectSend',
        'MaskClientIpInReceivedHeadersEnabled',
        'DisablePlusAddressInRecipients',
        'UnblockUnsafeSenderPromptEnabled'
    ];

    // Feature enablement fields
    const featureFields = [
        'ConnectorsEnabled',
        'BookingsEnabled',
        'OnlineMeetingsByDefaultEnabled',
        'OutlookPayEnabled',
        'WebPushNotificationsDisabled',
        'OutlookTextPredictionDisabled'
    ];

    // Calculate scores for each category
    const criticalScore = compareIntuneFields(baseline, client, criticalFields);
    const securityScore = compareIntuneFields(baseline, client, securityFields);
    const featureScore = compareIntuneFields(baseline, client, featureFields);

    // Weighted average (critical settings weighted more heavily)
    scores.overall = Math.round(
        criticalScore * 0.50 +
        securityScore * 0.30 +
        featureScore * 0.20
    );

    scores.conditions = criticalScore;
    scores.grantControls = securityScore;
    scores.sessionControls = featureScore;

    return scores;
};

/**
 * Compare Azure AD Authorization Policy
 */
const compareAzureADAuthorizationPolicy = (baseline, client) => {
    const scores = {
        conditions: 0,
        grantControls: 0,
        sessionControls: 100,
        state: 100,
        overall: 0,
        // ✅ Add custom labels for this policy type
        scoreLabels: {
            conditions: 'Guest Settings',
            grantControls: 'User Capabilities',
            sessionControls: 'Default Permissions'
        },
        policyTypeLabel: 'Azure AD Authorization'
    };

    // Guest and external user settings
    const guestFields = [
        'allowInvitesFrom',
        'guestUserRoleId',
        'allowEmailVerifiedUsersToJoinOrganization'
    ];

    // User capability settings
    const userCapabilityFields = [
        'allowedToSignUpEmailBasedSubscriptions',
        'allowedToUseSSPR',
        'allowUserConsentForRiskyApps',
        'blockMsolPowerShell'
    ];

    // Default user role permissions (nested object)
    let permissionsScore = 0;
    if (baseline.defaultUserRolePermissions && client.defaultUserRolePermissions) {
        const permissionsMatch = deepEqual(
            baseline.defaultUserRolePermissions,
            client.defaultUserRolePermissions
        );
        permissionsScore = permissionsMatch ? 100 : 0;
    }

    // Calculate category scores
    const guestScore = compareIntuneFields(baseline, client, guestFields);
    const userCapabilityScore = compareIntuneFields(baseline, client, userCapabilityFields);

    // Weighted average
    scores.overall = Math.round(
        guestScore * 0.30 +
        userCapabilityScore * 0.30 +
        permissionsScore * 0.40
    );

    scores.conditions = guestScore;
    scores.grantControls = userCapabilityScore;
    scores.sessionControls = permissionsScore;

    return scores;
};

/**
 * Generic policy comparison (fallback)
 */
const compareGenericPolicies = (baseline, client) => {
    // ✅ Use deepEqual instead of JSON.stringify
    const similarity = deepEqual(baseline, client) ? 100 : 0;

    return {
        conditions: similarity,
        grantControls: similarity,
        sessionControls: similarity,
        state: similarity,
        overall: similarity
    };
};

/**
 * ===========================================
 * Conditional Access Comparison Helpers
 * ===========================================
 */

/**
 * Compare policy conditions
 */
const compareConditions = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let matchCount = 0;
    let totalChecks = 0;

    const userScore = compareUserConditions(baseline.users, client.users);
    matchCount += userScore;
    totalChecks += 100;

    const appScore = compareApplicationConditions(baseline.applications, client.applications);
    matchCount += appScore;
    totalChecks += 100;

    const clientAppScore = compareArrayFields(baseline.clientAppTypes, client.clientAppTypes);
    matchCount += clientAppScore;
    totalChecks += 100;

    const platformScore = comparePlatformConditions(baseline.platforms, client.platforms);
    matchCount += platformScore;
    totalChecks += 100;

    const riskScore = compareRiskLevels(baseline, client);
    matchCount += riskScore;
    totalChecks += 100;

    const locationScore = compareLocationConditions(baseline.locations, client.locations);
    matchCount += locationScore;
    totalChecks += 100;

    return Math.round((matchCount / totalChecks) * 100);
};

const compareUserConditions = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let checks = 0;

    // Include users
    if (baseline.includeUsers || client.includeUsers) {
        checks++;
        score += compareArrayFields(baseline.includeUsers, client.includeUsers);
    }

    // ✅ SKIP excludeUsers - always different per tenant (break-glass accounts, etc.)

    // Include roles
    if (baseline.includeRoles || client.includeRoles) {
        checks++;
        score += compareArrayFields(baseline.includeRoles, client.includeRoles);
    }

    // ✅ SKIP excludeRoles - may differ per tenant

    // Include groups
    if (baseline.includeGroups || client.includeGroups) {
        checks++;
        // Can't match exact group IDs, so just check if both use groups
        score += (baseline.includeGroups?.length > 0 && client.includeGroups?.length > 0) ? 50 : 0;
    }

    // ✅ SKIP excludeGroups - may differ per tenant

    // If no checks were made, both policies have no user conditions - perfect match
    return checks > 0 ? Math.round(score / checks) : 100;
};

const compareApplicationConditions = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let checks = 0;

    if (baseline.includeApplications || client.includeApplications) {
        checks++;
        score += compareArrayFields(baseline.includeApplications, client.includeApplications);
    }

    if (baseline.excludeApplications || client.excludeApplications) {
        checks++;
        score += compareArrayFields(baseline.excludeApplications, client.excludeApplications);
    }

    return checks > 0 ? Math.round(score / checks) : 0;
};

const comparePlatformConditions = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let checks = 0;

    if (baseline.includePlatforms || client.includePlatforms) {
        checks++;
        score += compareArrayFields(baseline.includePlatforms, client.includePlatforms);
    }

    if (baseline.excludePlatforms || client.excludePlatforms) {
        checks++;
        score += compareArrayFields(baseline.excludePlatforms, client.excludePlatforms);
    }

    return checks > 0 ? Math.round(score / checks) : 0;
};

const compareRiskLevels = (baseline, client) => {
    let score = 0;
    let checks = 0;

    if (baseline.signInRiskLevels || client.signInRiskLevels) {
        checks++;
        score += compareArrayFields(baseline.signInRiskLevels, client.signInRiskLevels);
    }

    if (baseline.userRiskLevels || client.userRiskLevels) {
        checks++;
        score += compareArrayFields(baseline.userRiskLevels, client.userRiskLevels);
    }

    return checks > 0 ? Math.round(score / checks) : 0;
};

const compareLocationConditions = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let checks = 0;

    // Include locations
    if (baseline.includeLocations || client.includeLocations) {
        checks++;
        score += compareArrayFields(baseline.includeLocations, client.includeLocations);
    }

    // ✅ SKIP excludeLocations - always different per tenant (trusted IPs, named locations)

    // If no checks were made, both policies have no location conditions - perfect match
    return checks > 0 ? Math.round(score / checks) : 100;
};

const compareGrantControls = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let checks = 0;

    if (baseline.builtInControls || client.builtInControls) {
        checks++;
        score += compareArrayFields(baseline.builtInControls, client.builtInControls);
    }

    if (baseline.operator || client.operator) {
        checks++;
        score += (baseline.operator === client.operator) ? 100 : 0;
    }

    if (baseline.customAuthenticationFactors || client.customAuthenticationFactors) {
        checks++;
        score += compareArrayFields(baseline.customAuthenticationFactors, client.customAuthenticationFactors);
    }

    if (baseline.termsOfUse || client.termsOfUse) {
        checks++;
        score += compareArrayFields(baseline.termsOfUse, client.termsOfUse);
    }

    return checks > 0 ? Math.round(score / checks) : 0;
};

const compareSessionControls = (baseline, client) => {
    if (!baseline && !client) return 100;
    if (!baseline || !client) return 0;

    let score = 0;
    let count = 0;

    // Check each session control property using deep equality
    if (baseline.signInFrequency || client.signInFrequency) {
        count++;
        if (deepEqual(baseline.signInFrequency, client.signInFrequency)) {
            score++;
        }
    }

    if (baseline.persistentBrowser || client.persistentBrowser) {
        count++;
        if (deepEqual(baseline.persistentBrowser, client.persistentBrowser)) {
            score++;
        }
    }

    if (baseline.cloudAppSecurity || client.cloudAppSecurity) {
        count++;
        if (deepEqual(baseline.cloudAppSecurity, client.cloudAppSecurity)) {
            score++;
        }
    }

    if (baseline.applicationEnforcedRestrictions || client.applicationEnforcedRestrictions) {
        count++;
        if (deepEqual(baseline.applicationEnforcedRestrictions, client.applicationEnforcedRestrictions)) {
            score++;
        }
    }

    return count > 0 ? Math.round((score / count) * 100) : 100;
};

/**
 * Compare array fields using set-based comparison (order-insensitive)
 */
const compareArrayFields = (arr1, arr2) => {
    if (!arr1 && !arr2) return 100;
    if (!arr1 || !arr2) return 0;
    if (arr1.length === 0 && arr2.length === 0) return 100;

    // Convert arrays to sets for comparison (handles primitives and objects)
    const set1 = new Set(arr1.map(v => JSON.stringify(v)));
    const set2 = new Set(arr2.map(v => JSON.stringify(v)));

    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);

    return union.size > 0 ? Math.round((intersection.size / union.size) * 100) : 100;
};

/**
 * ===========================================
 * Fuzzy Matching & Best Matches
 * ===========================================
 */

/**
 * Find best matches for a baseline policy from client policies
 */
export const findBestMatches = (baselinePolicy, clientPolicies, threshold = 60) => {
    if (!baselinePolicy || !clientPolicies || clientPolicies.length === 0) {
        return [];
    }

    // Singleton policies should not use fuzzy matching
    if (isSingletonPolicy(baselinePolicy)) {
        debugLog('⚠️ Singleton policy should not use fuzzy matching:', baselinePolicy.Identity || baselinePolicy.displayName);
        return [];
    }

    const baselineType = detectPolicyType(baselinePolicy);
    debugLog(`🔍 Finding matches for ${baselineType}:`, baselinePolicy.displayName);

    // Filter to same type policies
    const sameTypePolicies = clientPolicies.filter(cp => areSameType(baselinePolicy, cp));
    debugLog(`📊 Found ${sameTypePolicies.length} client policies of type ${baselineType}`);

    if (sameTypePolicies.length === 0) {
        return [];
    }

    // Calculate similarity for each and filter by threshold
    const matches = sameTypePolicies
        .map(clientPolicy => ({
            policy: clientPolicy,
            scores: calculatePolicySimilarity(baselinePolicy, clientPolicy),
            displayName: clientPolicy.displayName || clientPolicy.name || clientPolicy.Name || 'Unknown',
            id: clientPolicy.id
        }))
        .filter(match => match.scores.overall >= threshold)
        .sort((a, b) => b.scores.overall - a.scores.overall);

    return matches;
};

/**
 * ===========================================
 * Match Explanation
 * ===========================================
 */

/**
 * Generate explanation of why policies match/don't match
 */
export const explainMatch = (baselinePolicy, clientPolicy, scores) => {
    const explanations = [];
    const policyType = detectPolicyType(baselinePolicy);

    if (scores.stateMismatch) {
        explanations.push('⚠️ State Mismatch: Policies have different enabled/disabled states');
    }

    // ✅ Route to appropriate explanation based on policy type
    if (scores.isIntunePolicy) {
        // Intune policy explanations
        if (scores.passwordSettings !== undefined) {
            if (scores.passwordSettings >= 90) {
                explanations.push('✅ Password settings match well');
            } else if (scores.passwordSettings >= 70) {
                explanations.push('⚠️ Password settings partially match');
            } else if (scores.passwordSettings < 70) {
                explanations.push('❌ Password settings differ significantly');
            }
        }

        if (scores.securitySettings !== undefined) {
            if (scores.securitySettings >= 90) {
                explanations.push('✅ Security settings match well');
            } else if (scores.securitySettings >= 70) {
                explanations.push('⚠️ Security settings partially match');
            } else if (scores.securitySettings < 70) {
                explanations.push('❌ Security settings differ significantly');
            }
        }

        if (scores.osSettings !== undefined) {
            if (scores.osSettings >= 90) {
                explanations.push('✅ OS requirements match well');
            } else if (scores.osSettings >= 70) {
                explanations.push('⚠️ OS requirements partially match');
            } else if (scores.osSettings < 70) {
                explanations.push('❌ OS requirements differ significantly');
            }
        }

        if (scores.settingsMatch !== undefined) {
            if (scores.settingsMatch >= 90) {
                explanations.push('✅ Configuration settings match well');
            } else if (scores.settingsMatch >= 70) {
                explanations.push('⚠️ Configuration settings partially match');
            } else if (scores.settingsMatch < 70) {
                explanations.push('❌ Configuration settings differ significantly');
            }
        }
    } else if (policyType === 'exchangeOrganizationConfig') {
        // Exchange Organization Config explanations
        if (scores.conditions >= 90) {
            explanations.push('✅ Critical Exchange settings match well');
        } else if (scores.conditions >= 70) {
            explanations.push('⚠️ Some Exchange settings differ');
        } else {
            explanations.push('❌ Exchange settings differ significantly');
        }

        if (scores.grantControls >= 90) {
            explanations.push('✅ Security settings match well');
        } else if (scores.grantControls >= 70) {
            explanations.push('⚠️ Security settings partially match');
        }
    } else if (policyType === 'azureADAuthorizationPolicy') {
        // Azure AD Authorization Policy explanations
        if (scores.conditions >= 90) {
            explanations.push('✅ Guest and external user settings match well');
        } else if (scores.conditions >= 70) {
            explanations.push('⚠️ Guest settings partially match');
        } else {
            explanations.push('❌ Guest settings differ significantly');
        }

        if (scores.grantControls >= 90) {
            explanations.push('✅ User capability settings match well');
        } else if (scores.grantControls >= 70) {
            explanations.push('⚠️ User capability settings partially match');
        }

        if (scores.overall < 70) {
            explanations.push('❌ Default user role permissions differ');
        }
    } else {
        // Conditional Access policy explanations (existing logic)
        if (scores.displayName !== undefined) {
            if (scores.displayName === 100) {
                explanations.push('✅ Display names match exactly');
            } else if (scores.displayName >= 80) {
                explanations.push('✅ Display names are very similar');
            } else if (scores.displayName >= 60) {
                explanations.push('⚠️ Display names are somewhat similar');
            }
        }

        if (scores.conditions !== undefined) {
            if (scores.conditions >= 90) {
                explanations.push('✅ Conditions are highly similar');
            } else if (scores.conditions >= 70) {
                explanations.push('⚠️ Conditions are partially similar');
            } else if (scores.conditions > 0) {
                explanations.push('❌ Conditions differ significantly');
            }
        }

        if (scores.grantControls !== undefined) {
            if (scores.grantControls >= 90) {
                explanations.push('✅ Grant controls match well');
            } else if (scores.grantControls >= 70) {
                explanations.push('⚠️ Grant controls partially match');
            } else if (scores.grantControls > 0) {
                explanations.push('❌ Grant controls differ');
            }
        }

        if (scores.sessionControls !== undefined && scores.sessionControls < 100) {
            if (scores.sessionControls >= 90) {
                explanations.push('✅ Session controls match well');
            } else if (scores.sessionControls >= 70) {
                explanations.push('⚠️ Session controls partially match');
            } else if (scores.sessionControls > 0) {
                explanations.push('❌ Session controls differ');
            }
        }
    }

    return explanations.join('\n');
};

/**
 * ===========================================
 * Exports
 * ===========================================
 */

export { isSingletonPolicy, detectPolicyType, areSameType };